import { Component, EventEmitter, OnDestroy, OnInit, ViewChild, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { lastValueFrom, map, Observable, of, startWith, Subscription, concatMap } from 'rxjs';
import 'src/app/extensions/admin-array-extension'
import { IBaseEntity } from 'src/app/model/base-entity.model';
import { ICountry } from 'src/app/model/country.model';
import { IOperator } from 'src/app/model/operator.model';
import { PopupData } from 'src/app/model/popup.model';
import { IRegion } from 'src/app/model/region.model';
import { IRig } from 'src/app/model/rig.model';
import { IWellEnvironment } from 'src/app/model/well-environment.model';
import { WellRequest } from 'src/app/model/well-request.model';
import { IdTokenClaims } from 'src/app/profile/profile.component';
import { BackendService, Suggestions } from 'src/app/services/backend.service';
import { environment } from '../../../environments/environment.dev';
import { WellRequestStatus_Options } from '../../model/status-enum.model';
import { RoutingService } from '../../services/routing.service';
import { EmailService } from '../../services/email.service';
import { StatusHelperService } from '../../services/status-helper.service';
import { IGroup } from '../../model/group.model';
import { IAdmin } from '../../model/admin.model';
import { PendingRequest } from '../../model/well-request-pending.model';
import { WellRequestAdmin } from '../../model/well-request-admin.model';
import { WellRequestRegionHandler } from './well-request-handler/well-request-handler-region';
import { WellRequestGroupHandler } from './well-request-handler/well-request-handler-group';
import { SubmitPopupComponent } from '../../controls/popup/submit/submit-popup.component';
import { WellRequestCountryHandler } from './well-request-handler/well-request-handler-country';
import { ChipComponent } from '../../controls/chip/chip.component';
import { SelectComponent } from '../../controls/select/select.component';

@Component({
    selector: 'app-well-request',
    templateUrl: './well-request.component.html',
    styleUrls: ['./well-request.component.css'],
    providers: [RoutingService],
    standalone: false
})
export class WellRequestComponent implements OnInit, OnDestroy {
  private _emailToken: AuthenticationResult | undefined
  public get regionControl(): AbstractControl<any, any> {return this.form.controls['region']};
  public get countryControl(): AbstractControl<any, any> { return this.form.controls['country'] };
  public get operatorControl(): AbstractControl<any, any> { return this.form.controls['operator'] };
  public get rigControl(): AbstractControl<any, any> { return this.form.controls['rig'] };
  public get groupControl(): AbstractControl<any, any> { return this.form.controls['group'] };
  public get adminControl(): AbstractControl<any, any> { return this.form.controls['admin'] };

  regionDataForDropdown: any = [];

  popupSubmitData: PopupData | undefined;
  popupUpdateData: PopupData | undefined;
  form: FormGroup;
  editForm: boolean = false;
  header: string | undefined;

  wellEnvironments: IWellEnvironment[] = [];
  regions: IRegion[] = [];
  countries: ICountry[] = [];
  filteredCountries: ICountry[] = [];
  groups: IGroup[] = [];
  filteredGroups: IGroup[] = [];
  selectedGroups: IGroup[] = [];
  admins: IAdmin[] = [];
  filteredAdmins: IAdmin[] = [];
  selectedAdmins: IAdmin[] = [];
  operators: IOperator[] = [];
  rigs: IRig[] = [];
  filteredRigs: IRig[] = [];
  wellRequest: WellRequest | undefined;

  initialAdmins: IAdmin[] = [];
  previousCountryId: string | undefined;
  oldRegion: IBaseEntity | undefined;
  oldCountry: ICountry | undefined;
  countryHandler!: WellRequestCountryHandler;
  regionHandler!: WellRequestRegionHandler;
  groupHandler!: WellRequestGroupHandler;
  closeEvent = new EventEmitter<boolean>();

  @ViewChildren(ChipComponent) chipComponents!: QueryList<ChipComponent>;
  @ViewChildren(SelectComponent) selectComponents!: QueryList<SelectComponent>;

  private rigOwnerManuallyEntered: boolean = false;
  private rigTypeManuallyEntered: boolean = false;
  private originalRigOwner: string = '';
  private originalRigType: string = '';
  private approvalSubscription: Subscription | undefined;
  private _currentStatus: WellRequestStatus_Options | undefined;
  private _isStatusChanging: boolean = false;
  private _statusChangesSubscription: Subscription | undefined;

  orientationOptions = [
    'UNDETERMINED',
    'DIRECTIONAL',
    'VERTICAL',
    'HORIZONTAL'
  ];

  wellTypeOptions = [
    'OIL & GAS',
    'OIL',
    'INJECTOR',
    'GAS',
    'OTHER',
    'STRAT TEST',
    'DRY HOLE',
    'WATER',
    'SERVICE',
    'DISPOSAL',
    'OBSERVATION',
    'EOR',
    'STORAGE',
    'CBM',
    'GAS INJECTOR',
    'SWD',
    'BRINE',
    'WATER INJECTOR',
    'CO2'
  ];

  uomOptions = [
    'ft',
    'meters'
  ]

  filteredOrientationOptions: Observable<string[]> = of([]);
  filteredWellTypeOptions: Observable<string[]> = of([]);
  filteredUomOptions: Observable<string[]> = of([]);

  filteredDepthOptions: Observable<string[]> = of([]);
  filteredStateOptions: Observable<string[]> = of([]);
  filteredDistrictOptions: Observable<string[]> = of([]);
  filteredWellNumOptions: Observable<string[]> = of([]);
  filteredRigOwnerOptions: Observable<string[]> = of([]);
  filteredRigTypeOptions: Observable<string[]> = of([]);

  constructor(
    readonly _routingService: RoutingService,
    readonly _formBuilder: FormBuilder,
    readonly _backendService: BackendService,
    readonly _msalService: MsalService,
    readonly _dialog: MatDialog,
    readonly _emailService: EmailService,
    readonly _snackBar: MatSnackBar,
    private readonly _statusHelper: StatusHelperService) {
    this.form = this.getFormGroup();
    this.initSubmitPopup();
    this._statusChangesSubscription = this.statusChanges();
  }

  private suggestions: Suggestions = {}

  public operatorDisplayFormat = (item: IBaseEntity): string => {
    return item && item.name && item.id ? `${item.name} - ${item.id}` : (item?.name || '');
  }

  public get rigOwnerControl(): AbstractControl<any, any> { return this.form.controls['rigOwner'] };
  public get rigTypeControl(): AbstractControl<any, any> { return this.form.controls['rigType'] };

  public get rigSourceControl(): AbstractControl<any, any> { return this.form.controls['rigSource'] };

  public getRigSourceDisplay(): string {
    const rigSource = this.rigSourceControl.value;
    
    const hasManualEntry = this.rigOwnerManuallyEntered || this.rigTypeManuallyEntered;
    
    if (rigSource === 'Enverus' && hasManualEntry) {
      return "ENVERUS, LOGIX™";
    }
    
    return rigSource?.toUpperCase() || '';
  }

  public get wellSourceControl(): AbstractControl<any, any> { return this.form.controls['wellSource'] };

  public getWellSourceDisplay(): string {
    const wellSource = this.wellSourceControl.value;
    if (!wellSource) {
      return 'LOGIX™';
    }
    const sources = wellSource.split(',').map((s: string) => s.trim().toUpperCase());
    return sources.join(', ');
  }

  public get isRiglessControl(): AbstractControl<any, any> { return this.form.controls['isRigless'] };

  private getFormGroup(): FormGroup {
    let coordinatePattern = new RegExp("^-?\\d+\\.?");
    let depthPattern = new RegExp("^\\d+\\.?");

    return this._formBuilder.group({
      name: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
      wellEnvironment: ['', Validators.required],
      region:  [{value: '', disabled: true}, Validators.required],
      country: [{value: '', disabled: true}, Validators.required],
      group: [{value: '', disabled: true}, Validators.required],
      admin: [{value: '', disabled: true}, Validators.required],
      state: ['', Validators.maxLength(50)],
      district: ['', Validators.maxLength(50)],
      operator: [{value: '', disabled: true}],
      isRigless: [false],
      rig: [{value: '', disabled: true}, Validators.required],
      latitude: ['', Validators.compose([Validators.required, Validators.min(-90), Validators.max(90), Validators.pattern(coordinatePattern)])],
      longitude: ['', Validators.compose([Validators.required, Validators.min(-180), Validators.max(180), Validators.pattern(coordinatePattern)])],
      rigOwner: ['', Validators.maxLength(140)],
      rigType: ['', Validators.maxLength(140)],
      rigSource: [{ value: '', disabled: true }],
      wellSource: [{ value: 'LOGIX™', disabled: true }],
      wellOrientation: ['', Validators.maxLength(20)],
      wellUOM: ['', Validators.compose([Validators.required, Validators.maxLength(12)])],
      wellDepth: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(999999), Validators.pattern(depthPattern)])],
      wellType: ['', Validators.maxLength(20)],
      wellApiNumber: ['', Validators.maxLength(200)],
      comments: ['', Validators.compose([Validators.maxLength(250)])]
    });
  }

  ngOnInit() {
    this.initHandlers();
    this.subscribeToEvents();
    this.preselectFromRoute();
    this.setWellEnvironments();
    let wellRequestId: number = this._routingService.getWellRequestId();
    this.editForm = wellRequestId !== -1;
    this.setRegions()
      .then(() => this.setCountries()
      .then(() => this.setGroups())
      .then(() => this.setAdmins())
      .then(() => this.setOperators())
      .then(() => this.setRigs())
      .then(() => this.loadWellsRequest(wellRequestId)));
    this.setEmailToken();
    this.initUiData(wellRequestId !== -1);

    this.filteredOrientationOptions = this.form.get('wellOrientation')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.orientationOptions))
      );

    this.filteredWellTypeOptions = this.form.get('wellType')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.wellTypeOptions))
      );
      
    this.filteredUomOptions = this.form.get('wellUOM')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.uomOptions))
      );

    this.filteredWellNumOptions = this.form.get('wellApiNumber')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.wellNumbers ?? []))
      );

    this.filteredStateOptions = this.form.get('state')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.states ?? []))
      );

    this.filteredDistrictOptions = this.form.get('district')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.districts ?? []))
      );

    this.filteredDepthOptions = this.form.get('wellDepth')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.depths ?? []))
      );

    this.filteredRigOwnerOptions = this.form.get('rigOwner')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.rigOwners ?? []))
      );

    this.filteredRigTypeOptions = this.form.get('rigType')!.valueChanges
      .pipe(
        startWith(''),
        map(val => this.filter(val, this.suggestions?.rigTypes ?? []))
      );

    this._backendService.getSuggestions()
      .subscribe((suggestions: Suggestions) => {
        this.suggestions = suggestions;

        this.form.get('wellApiNumber')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        this.form.get('state')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        this.form.get('district')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        this.form.get('wellDepth')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        this.form.get('rigOwner')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        this.form.get('rigType')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });

        console.log('Suggestions: ', this.suggestions);
      });
  }

  filter(val: string | null, options: string[]): string[] {
    if (!val) return options;
    const filterValue = val?.toLowerCase();

    if (!filterValue) return options;
    
    return options.filter(option =>
      option.toLowerCase().indexOf(filterValue) === 0);
  }

  getAdminsByGroups(groups: IGroup[] | undefined) {
    if (!groups || groups.length == 0) {
      this.resetAdmins(groups);
      return;
    }

    this.filteredAdmins = this.admins.filter(x => x.adminGroups
      && x.adminGroups.some(ids => groups.map(id => id.id)
        .includes(ids.groupId)))
      .reduce((acc: IAdmin[], curr) => {
        if (!acc.map(a => a.id).includes(curr.id)) {
          acc.push(curr);
        }
        return acc;
      }, []);

    this.selectedAdmins = this.selectedAdmins
      .filter(x => x.adminGroups?.find(a => groups.some(g => g.id == a.groupId)));
  }

  groupsSelectionChanged(event: any) {
    let groups = event as IGroup[];
    this.groupHandler.groupSelectedHandler(groups);
  }

  async groupsRemoveChanged(event: any) {
    let beforeRemove = event.beforeRemove as IGroup[];
    await this.groupHandler.groupRemoveHandler(beforeRemove);
  }

  getFilteredCountries<T>(regionId: T | undefined): ICountry[] {
    return this.countries.filter(country => country.regionId == regionId);
  }

  patchCountry(countryId: string): ICountry | undefined {
    const country = this.countries.find(x => x.id == countryId)
    this.countryControl.setValue(country, { emitEvent: false, });
    return country;
  }

  submitHandler(event: any) {
    let isSubmited = event as boolean;
    if (isSubmited)
      this.submitPopup();
  }

  isAdminRemovable = (event: any) => {
    let admin = event as IAdmin;
    return !admin.isTookAction
  }

  private initHandlers() {
    this.regionHandler = new WellRequestRegionHandler(this);
    this.countryHandler = new WellRequestCountryHandler(this);
    this.groupHandler = new WellRequestGroupHandler(this);
  }

  private subscribeToEvents() {
    this.regionControl.valueChanges.subscribe((regionData: any) => {
      this.regionHandler.regionHandler(regionData);
    });

    this.countryControl.valueChanges.subscribe((country: any) => {
      this.countryHandler.countryHandler(country);
   });

    this.groupControl.valueChanges.subscribe((groupData: any) => {
      this.groupHandler.groupChangeHandler(groupData);
    });

    this.rigControl.valueChanges.subscribe((rigData: any) => {
      this.rigHandler(rigData);
    });


    this.rigOwnerControl.valueChanges.subscribe((value: string) => {
      if (!this.rigOwnerControl.disabled) {
        const currentValue = value || '';
        if (currentValue === this.originalRigOwner) {
          this.rigOwnerManuallyEntered = false;
        } else {
          this.rigOwnerManuallyEntered = true;
        }
      }
    });

    this.rigTypeControl.valueChanges.subscribe((value: string) => {
      if (!this.rigTypeControl.disabled) {
        const currentValue = value || '';
        if (currentValue === this.originalRigType) {
          this.rigTypeManuallyEntered = false;
        } else {
          this.rigTypeManuallyEntered = true;
        }
      }
    });

    this.isRiglessControl.valueChanges.subscribe((isRigless: boolean) => {
      this.handleRiglessChange(isRigless);
    });
  }

  private handleRiglessChange(isRigless: boolean): void {
    if (isRigless) {
      this.rigControl.clearValidators();
      this.rigControl.reset();
      this.rigControl.disable();
      this.rigOwnerControl.reset();
      this.rigTypeControl.reset();
      this.rigSourceControl.reset();
    } else {
      this.rigControl.setValidators(Validators.required);
      this.rigControl.enable();
      this.rigControl.markAsTouched();
    }
    this.rigControl.updateValueAndValidity();

    setTimeout(() => {
      this.selectComponents?.forEach(select => select.markAsTouched());
    }, 0);
  }

  private rigHandler(rigData: any) {
    if (rigData?.id) {
      const rigOwnerValue = rigData.rigOwner || '';
      const rigTypeValue = rigData.rigType || '';
      
      this.originalRigOwner = rigOwnerValue;
      this.originalRigType = rigTypeValue;
      
      this.rigOwnerControl.patchValue(rigOwnerValue);
      this.rigTypeControl.patchValue(rigTypeValue);
      this.rigSourceControl.patchValue(rigData.rigSource || 'N/A');

      this.rigOwnerManuallyEntered = false;
      this.rigTypeManuallyEntered = false;
    } else {
      this.rigOwnerControl.reset();
      this.rigTypeControl.reset();
      this.rigSourceControl.reset();
      this.originalRigOwner = '';
      this.originalRigType = '';
      this.rigOwnerManuallyEntered = false;
      this.rigTypeManuallyEntered = false;
    }

    if (rigData?.id && this.groupControl.value?.length == 0) {
      let rigGroup = this.groups.find(x => x.id == rigData?.groupId);
      if (!rigGroup)
        return;

      this.groupControl.patchValue(rigGroup, { emitEvent: false });
      this.selectedGroups = [rigGroup]
      this.filteredRigs = this.rigs.filter(x => x.groupId == rigGroup?.id);
    }
  }

  private loadWellsRequest(wellRequestId: number): void {
    if (wellRequestId === -1) {
      this.resetFormForNewRequest();
      return;
    }

    this._backendService.getWellRequest(wellRequestId.toString()).subscribe(
      data => {
        this.wellRequest = data;
        let operatorId: string = data.operatorId ?? "";
        let rigId: string = data.rigId ?? "";
        let regionId: string = data.regionId?.toString() ?? "";
        let countryId: string = data.countryId?.toString() ?? "";
        this.preseletOperator(operatorId);
        this.preselectRegion(regionId);
        this.preselectCountry(countryId);
        this.preselectedGroups(this.wellRequest.groups);
        this.preselectedAdmins(this.wellRequest.admins);
        const isRigless = !rigId;
        this.form.controls['isRigless'].setValue(isRigless);
        if (!isRigless) {
          this.preselectRig(rigId);
        }
        this.form.controls['name'].setValue(data.name);
        this.form.controls['wellEnvironment'].setValue(data.rtsEnvironmentId);
        this.form.controls['state'].setValue(data.state);
        this.form.controls['district'].setValue(data.district);
        this.form.controls['longitude'].setValue(data.longitude);
        this.form.controls['latitude'].setValue(data.latitude);
        this.form.controls['wellApiNumber'].setValue(data.usCanadaWellId);
        this.form.controls['wellType'].setValue(data.wellType);
        this.form.controls['wellDepth'].setValue(data.wellDepth);
        this.form.controls['wellUOM'].setValue(data.wellUOM);
        this.form.controls['wellOrientation'].setValue(data.wellOrientation);
        this.regionControl.enable();
        this.countryControl.enable();
        this.groupControl.enable();
        this.operatorControl.enable();
        this.rigControl.enable();
        this.adminControl.enable();

        if (data.manualRigOwner) {
          this.rigOwnerControl.setValue(data.manualRigOwner);
          this.rigOwnerManuallyEntered = true;
          this.rigOwnerControl.enable();
        }

        if (data.manualRigType) {
          this.rigTypeControl.setValue(data.manualRigType);
          this.rigTypeManuallyEntered = true;
          this.rigTypeControl.enable();
        }

        this.highlightInvalidFields();
      }
    );
  }

  private initUiData(isEdit: boolean) {
    if (!isEdit)
      this.header = "New Well Request";
  }

  private resetFormForNewRequest(): void {
    this.wellRequest = undefined;
    this.selectedGroups = [];
    this.selectedAdmins = [];
    this.filteredAdmins = [];
    this.filteredRigs = this.rigs.slice();
    this.rigOwnerManuallyEntered = false;
    this.rigTypeManuallyEntered = false;
    this.originalRigOwner = '';
    this.originalRigType = '';
    this.initialAdmins = [];

    this.form.controls['name'].reset();
    this.form.controls['state'].reset();
    this.form.controls['district'].reset();
    this.form.controls['operator'].reset();
    this.form.controls['rig'].reset();
    this.form.controls['latitude'].reset();
    this.form.controls['longitude'].reset();
    this.form.controls['rigOwner'].reset();
    this.form.controls['rigType'].reset();
    this.form.controls['rigSource'].reset();
    this.form.controls['wellOrientation'].reset();
    this.form.controls['wellUOM'].reset();
    this.form.controls['wellDepth'].reset();
    this.form.controls['wellType'].reset();
    this.form.controls['wellApiNumber'].reset();
    this.form.controls['comments'].reset();
    this.form.controls['group'].reset();
    this.form.controls['admin'].reset();

    this.form.controls['isRigless'].setValue(false);
    this.form.controls['wellSource'].setValue('LOGIX™');
    this.setWellEnvironmentDefault();
    this.highlightInvalidFields();
  }

  private preselectFromRoute(): void {
    this._routingService.activatedRoute.queryParams.subscribe(params => {
      let operatorId: string = params['operatorId'];
      let rigId: string = params['rigId'];
      this.preseletOperator(operatorId);
      this.preselectRig(rigId);
    });
  }

  private setWellEnvironments(): void {
    this._backendService.getWellEnvironment().subscribe((wellEnvironments : IWellEnvironment[]) => {
      this.wellEnvironments = wellEnvironments;
      this.setWellEnvironmentDefault();
    });
  }

  private setWellEnvironmentDefault(): void {
    if (this.wellEnvironments.length == 1) {
      let wellEnvironmentField = this.form.controls['wellEnvironment'];
      wellEnvironmentField.patchValue(this.wellEnvironments[0].id);
      wellEnvironmentField?.disable();
    }
  }

  private preseletOperator(operatorId: string): void {
    if (operatorId) {
      this.getOperator(operatorId, (operator: IOperator) => {
        this.form.controls['operator'].patchValue(operator);
      });
    }
  }

  private getOperator(id: string, callback: Function): void {
    let operator: IOperator | undefined = this.operators.find(operator => operator.id === id);
    if (!operator) {
      this._backendService.getOperator(id).subscribe((operator : IOperator) => {
        if (!this.operators.some(x => x.id === operator.id))
          this.operators.push(operator);
        callback(operator);
      });
    }
    else {
      callback(operator);
    }
  }

  private preselectRig(rigId: string): void {
    if (rigId) {
      rigId = rigId?.toLowerCase();
      this.getRig(rigId, (rig: IRig) => {
        this.form.controls['rig'].patchValue(rig);
      });
    }
  }

  private getRig(id: string, callback: Function): void {
    let rig: IRig | undefined = this.rigs.find(rig => rig.id === id);
    if (!rig) {
      this._backendService.getRig(id).subscribe((rig : IRig) => {
        if (!this.rigs.some(x => x.id === rig.id)) {
          this.rigs.push(rig);
          this.filteredRigs.push(rig)
        }
        callback(rig);
      });
    }
    else {
      callback(rig);
    }
  }

  private preselectRegion(regionId: string): void {
    if (regionId) {
      this.get(regionId, this.regions, this._backendService.getRegion, (region: IRegion) => {
        this.regionControl.patchValue(region.id, {emitEvent: false});
        this.filteredCountries = this.getFilteredCountries(region.id);
      });
    }
  }

  private preselectCountry(countryId: string): void {
    if (countryId) {
      this.get(countryId, this.countries, this._backendService.getCountry, (country: ICountry) => {
        this.countryControl.patchValue(country, {emitEvent: false});
      });
    }
  }

  private preselectedGroups(groups: IGroup[] | undefined) {
    if (groups) {
      this.groupControl.patchValue(groups, { emitEvent: false });
      this.selectedGroups = groups;
      this.groupHandler.getRigsByGroups(groups);
      this.getAdminsByGroups(groups);
    }
  }

  private preselectedAdmins(adminsId: WellRequestAdmin[] | undefined) {
    if (adminsId) {
      let adminSelected = this.admins.getAdmins(adminsId);
      this.adminControl.patchValue(adminSelected, { emitEvent: false });
      this.selectedAdmins = adminSelected;
      this.initialAdmins = this.selectedAdmins.slice();
    }
  }


  private get(id: string, arr: IBaseEntity[], action: Function, callback: Function): void {
    let value: IBaseEntity | undefined = arr.find(item => item.id == id);
    if (!value) {
      action(id).subscribe((value: IBaseEntity) => {
        if (!arr.some(x => x.id === value.id))
          arr.push(value);

        callback(value);
      });
    }
    else {
      callback(value);
    }
  }

  private async setRegions(): Promise<void> {
    this.regions = await lastValueFrom(this._backendService.getRegions());
    this.regionControl.updateValueAndValidity({onlySelf: true, emitEvent: false});
    this.regionDataForDropdown=[];
    this.regions.forEach(item=>{
      this.regionDataForDropdown.push({id:item.id,name:item.code})
    })
    if (!this.editForm)
      this.regionControl.enable();
  }

  private async setCountries(): Promise<void>  {
    this.countries = this.filteredCountries = await lastValueFrom(this._backendService.getCountries());
    if (!this.editForm)
      this.countryControl.enable();
    this.countryControl.updateValueAndValidity({onlySelf: true, emitEvent: false});
  }

  private async setGroups(): Promise<void> {
    this.groups = this.filteredGroups = await lastValueFrom(this._backendService.getGroups());
    if (!this.editForm)
      this.groupControl.enable();
    this.groupControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private async setAdmins(): Promise<void> {
    this.admins = await lastValueFrom(this._backendService.getAdmins());
    if (!this.editForm)
      this.adminControl.enable();
    this.adminControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private async setOperators(): Promise<void> {
    this.operators = await lastValueFrom(this._backendService.getOperators());
    if (!this.editForm)
      this.operatorControl.enable();
    this.operatorControl.updateValueAndValidity({onlySelf: true, emitEvent: true});
  }

  private async setRigs(): Promise<void> {
    this.rigs = this.filteredRigs = await lastValueFrom(this._backendService.getRigs());
    if (!this.editForm)
      this.rigControl.enable();
    this.rigControl.updateValueAndValidity({onlySelf: true, emitEvent: true});
  }

  private setEmailToken(): void {
    const accessTokenRequest = {
      scopes: environment.msGraphConfig.scopes
    };
    this._msalService.acquireTokenSilent(accessTokenRequest).subscribe({
      next: (response: AuthenticationResult) => {
        this._emailToken = response;
      }
    });
  }

  private submitPopup() {
    let claims: IdTokenClaims | undefined = this._msalService.instance.getActiveAccount()?.idTokenClaims;
    if (claims && claims.samaccountname) {
      let wellRequest: WellRequest = this.getWellRequest();
      wellRequest.userId = claims.samaccountname;
      if (this.editForm) {
        wellRequest.wellRequestStatusId = WellRequestStatus_Options.Approved
        this._backendService.updateWellRequest(wellRequest).subscribe({
          next: () => this.postWellRequestStep(wellRequest.id),
          error: (error) => {
            if (error.status == 409)
              this.wellNameValidation(error.error);
          }
        });
      }
      else
        this._backendService.createWellRequest(wellRequest).subscribe({
          next: (wellRequestId: bigint) => this.postWellRequestStep(wellRequestId),
          error: (error) => {
            if (error.status == 409)
              this.wellNameValidation(error.error);
          }
        });
    }
    else {
      this._msalService.loginRedirect();
    }  
  }

  private postWellRequestStep(wellRequestId: bigint | undefined) {
    if (wellRequestId) {
      this._emailService.EmailNotify(wellRequestId, true)
      let newAdmins = this.selectedAdmins.exceptAdmins(this.initialAdmins);

      if (newAdmins.length > 0 && this.initialAdmins.length > 0)
        this._emailService.EmailNotify(BigInt(wellRequestId),
                                       true,
                                       newAdmins);
      this._routingService.redirectToDefault();
    }
  }

  get uniqueNWA() {
    const unique = [];
    const map = new Map();
    for (const group of this.selectedGroups) {
      if (!map.has(group.nwa)) {
        map.set(group.nwa, true);
        unique.push(group);
      }
    }
    return unique;
  }

  private async resetAdmins(groups: IGroup[] | undefined) {
    if (!groups || groups?.length == 0) {
      this.adminControl.reset();
      this.selectedAdmins = [];
      this.filteredAdmins = []
      return;
    }
  }

  private getWellRequest(): WellRequest {
    let value: any = this.form.getRawValue();
    let wellRequest = new WellRequest();
    if (this.editForm) {
      wellRequest.id = this.wellRequest?.id
    }
    wellRequest.name = value.name;
    wellRequest.rtsEnvironmentId = value.wellEnvironment;
    wellRequest.countryId = value.country.id;
    wellRequest.regionId = value.region.id;
    wellRequest.state = value.state;
    wellRequest.district = value.district;
    wellRequest.operatorId = value.operator.id;
    wellRequest.rigId = value.isRigless ? null : value.rig?.id;
    wellRequest.latitude = value.latitude;
    wellRequest.longitude = value.longitude;
    wellRequest.groups = value.group;

    wellRequest.wellDepth = value.wellDepth;
    wellRequest.wellUOM = value.wellUOM;
    wellRequest.wellOrientation = value.wellOrientation;
    wellRequest.wellSource = "LOGIX™";
    wellRequest.wellUOM = value.wellUOM;
    wellRequest.wellType = value.wellType;
    wellRequest.usCanadaWellId = value.wellApiNumber;

    if (this.rigOwnerManuallyEntered && value.rigOwner) {
      wellRequest.manualRigOwner = value.rigOwner;
    }

    if (this.rigTypeManuallyEntered && value.rigType) {
      wellRequest.manualRigType = value.rigType;
    }

    if (wellRequest.groups)
      wellRequest.admins = (value.admin as IAdmin[])
                              .getWellRequestAdmins(wellRequest.groups);
    return wellRequest;
  }

  private wellNameValidation(message: string) {
    
    this._snackBar.open(message, "Close ✕", {
      duration: 10000,
      verticalPosition: 'bottom',
      horizontalPosition: 'end',  
      panelClass: "well-name-snackbar"
    });
  }

  private setNotValid(control: AbstractControl<any>) {
     control.setErrors({ "invalid": true });
     control.markAllAsTouched();
  }

  createPendingRequest(statusToUpdate: WellRequestStatus_Options, wellRequestId: bigint): Observable<void> {
    const pendingRequest = new PendingRequest();
    pendingRequest.statusToUpdate = statusToUpdate;
    pendingRequest.wellRequestId = wellRequestId;
    pendingRequest.comments = this.form.getRawValue().comments?.toString() || "";

    return this._backendService.approveWellRequest(pendingRequest)
      .pipe(
        concatMap(async () => {
          await this._emailService.EmailNotifyAll(wellRequestId, pendingRequest.statusToUpdate)
        }),
        map(() => void 0)
      );
  }

  ngOnDestroy(): void {
    this.approvalSubscription?.unsubscribe();
    this._statusChangesSubscription?.unsubscribe();
  }
  private initSubmitPopup() {
    this.popupSubmitData = new PopupData();
    this.popupSubmitData.header = "Submit Well Request";
    this.popupSubmitData.textInfo = `
      <span>
        Please note that after clicking <strong>SUBMIT REQUEST,<br/>
        additional changes</strong> to the request <strong>will not be possible</strong><br/>
        until it is <strong>reviewed by the administrators.</strong>
      </span>
    `;
    this.popupSubmitData.textSubmit = "YES, SUBMIT REQUEST"
  }

  back(): void {
    this._routingService.back();
  }

  openDialogApprove(): void {
    if (this.form?.invalid)
      return;

    let popupData = new PopupData();
    popupData.header = "Approve Well Request";
    popupData.textInfo = `
      <span>
        Please note that after clicking <strong>APPROVE REQUEST,<br/>
        additional changes</strong> to the request <strong>will not be possible.</strong>
      </span>
    `;
    popupData.textSubmit = "YES, APPROVE REQUEST";

    const dialogRef = this._dialog.open(SubmitPopupComponent, { data: { popupData: popupData } });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.wellRequest?.id) {
        const statusToUpdate = WellRequestStatus_Options.ApprovedActive;
        
        this.approvalSubscription = this.createPendingRequest(statusToUpdate, this.wellRequest.id)
          .subscribe({
            next: () => {
              this.submitPopup();
            },
            error: (error) => {
              console.error('Error creating pending request:', error);
              this._snackBar.open('Failed to approve request. Please try again.', 'Close ✕', {
                duration: 5000,
                verticalPosition: 'bottom',
                horizontalPosition: 'end'
              });
            }
          });
      }
    });
  }

  onCommentsEnter(event: Event) {
    event.preventDefault();
  }

  private highlightInvalidFields(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    });

    setTimeout(() => {
      this.chipComponents?.forEach(chip => chip.markAsTouched());
      this.selectComponents?.forEach(select => select.markAsTouched());
    }, 0);
  }

  private statusChanges(): Subscription {
    return this._statusHelper.statusItem$.subscribe((data) => {
      this._currentStatus = data;

      if (!this.wellRequest || data == this.wellRequest?.currentStatusId) {
        return;
      }

      if (this._isStatusChanging) {
        return;
      }

      if (this.wellRequest?.id) {
        this._isStatusChanging = true;
        
        this.createPendingRequest(data, this.wellRequest.id)
          .subscribe({
            complete: () => {
              this._isStatusChanging = false;
              this._snackBar.open('Status updated successfully', 'Close ✕', {
                duration: 3000,
                verticalPosition: 'bottom',
                horizontalPosition: 'end'
              });
            },
            error: (error) => {
              this._isStatusChanging = false;
              console.error('Error creating pending request:', error);
              this._snackBar.open('Failed to update status. Please try again.', 'Close ✕', {
                duration: 5000,
                verticalPosition: 'bottom',
                horizontalPosition: 'end'
              });
            }
          });
      }
    });
  }
}
